import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import {
  getQueryKey,
  useGqlMutation,
  useOrgId,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import { organisationRecordQuery } from "@/app/Organisations/GraphQL/organisationRecordQuery";
import { Checkbox } from "@/components/ui/checkbox";
import { FeatureFlagType } from "@/gql/graphql";
import { featureFlagMutation } from "@/app/Meta/GraphQL/featureFlagMutation.grahpql";
import { useQueryClient } from "@tanstack/react-query";

export function FeatureFlagDialog({
  organisationId,
}: {
  organisationId: string;
}) {
  return (
    <BaseDialog title="Feature Flags">
      <div className="space-y-6">
        <FeatureFlagCheckbox
          organisationId={organisationId}
          featureFlagType={FeatureFlagType.VariableRelocationRate}
          label="Variable Relocation Rate"
          description="Allow the organisation to change the relocation rate"
        />

        <FeatureFlagCheckbox
          organisationId={organisationId}
          featureFlagType={FeatureFlagType.InvoiceOverdueBanner}
          label="Invoice banner"
          description="Show overdue invoice banner when an invoice has not been paid"
        />

        <FeatureFlagCheckbox
          organisationId={organisationId}
          featureFlagType={FeatureFlagType.BlockWhenInvoiceOverdue}
          label="Block when invoice overdue"
          description="Block access to the system with a modal when invoices are overdue"
        />

        <FeatureFlagCheckbox
          organisationId={organisationId}
          featureFlagType={FeatureFlagType.BookingAcceptedState}
          label="Booking Accepted State"
          description="Allow the organisation to put the booking in an accepted state"
        />

        <FeatureFlagCheckbox
          organisationId={organisationId}
          featureFlagType={FeatureFlagType.GapRentals}
          label="Gap Rentals"
          description="Show gap rentals in the navigation and settings"
        />

        <FeatureFlagCheckbox
          organisationId={organisationId}
          featureFlagType={FeatureFlagType.JucyIntegration}
          label="Jucy Integration"
          description="Enable the Jucy integration"
        />
        <FeatureFlagCheckbox
          organisationId={organisationId}
          featureFlagType={FeatureFlagType.RcmIntegration}
          label="RCM Integration"
          description="Enable the RCM integration"
        />

        <FeatureFlagCheckbox
          organisationId={organisationId}
          featureFlagType={FeatureFlagType.CampermanIntegration}
          label="Camperman Integration"
          description="Enable the Camperman integration"
        />

        <FeatureFlagCheckbox
          organisationId={organisationId}
          featureFlagType={FeatureFlagType.CampermanGapFillerIntegration}
          label="Camperman Gap Filler Integration"
          description="Enable the Camperman gap filler integration"
        />

        <FeatureFlagCheckbox
          organisationId={organisationId}
          featureFlagType={FeatureFlagType.RoadsurferIntegration}
          label="Roadsurfer Integration"
          description="Enable the roadsurfer deal sheet integration"
        />
      </div>
    </BaseDialog>
  );
}

function FeatureFlagCheckbox({
  featureFlagType,
  label,
  description,
  organisationId,
}: {
  featureFlagType: FeatureFlagType;
  label: string;
  description: string;
  organisationId: string;
}) {
  const queryClient = useQueryClient();
  const orgId = useOrgId();
  const { data } = useSuspenseGqlQuery(organisationRecordQuery, {
    id: organisationId,
  });
  const { mutateAsync, isPending } = useGqlMutation(featureFlagMutation, {
    onSuccess: () => {
      return queryClient.invalidateQueries({
        queryKey: getQueryKey(
          organisationRecordQuery,
          { id: organisationId },
          orgId,
        ),
      });
    },
  });

  const featureFlags = data.organisation.featureFlags;

  const isFlagChecked = !!featureFlags.find(
    (flag) => flag.type === featureFlagType,
  );

  return (
    <div className="items-top flex space-x-2">
      <Checkbox
        id={featureFlagType}
        disabled={isPending}
        checked={isFlagChecked}
        onCheckedChange={() => {
          mutateAsync({
            input: {
              organisation_id: organisationId,
              type: featureFlagType,
              enabled: !isFlagChecked,
            },
          });
        }}
      />
      <div className="grid gap-1.5 leading-none">
        <label
          htmlFor={featureFlagType}
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
        >
          {label}
        </label>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
    </div>
  );
}
